import { useMemo, useCallback } from 'react';
import { UPLOAD_ACCESS_CONFIG } from '../config/uploadAccessConfig';
import {
  isUserAllowed,
  detectTriggerMatch,
  stripTriggers,
} from '../utils/uploadAccess';

/**
 * Derives upload-trigger state for the chat input.
 *
 *   isOpen        boolean   — should the upload UI be visible?
 *   activeRule    object?   — the rule whose trigger matched (or null
 *                              when in always-open mode / no match)
 *   sanitize(text)          — returns `text` with all triggers of the
 *                              matched rule removed; identity when closed
 *                              or in always-open mode
 *
 * Always-open mode kicks in when `config.rules` is null/empty — the UI is
 * visible regardless of input text or the user's access. Callers should
 * fall back to default upload constraints (e.g. maxFiles: 3, accept:
 * 'image/*') when `activeRule` is null.
 *
 * Config is parameterized so tests can swap it freely. Production code
 * passes nothing and uses the singleton config import.
 */
export function useUploadTrigger(text, user, config = UPLOAD_ACCESS_CONFIG) {
  const rules = config?.rules;
  const access = config?.access;
  const noRules = !Array.isArray(rules) || rules.length === 0;

  const allowed = useMemo(() => {
    if (noRules) return true; // always-open bypasses the access gate
    return isUserAllowed(user, access);
  }, [user, access, noRules]);

  const matchedRule = useMemo(() => {
    if (noRules || !allowed) return null;
    return detectTriggerMatch(text, rules);
  }, [text, rules, allowed, noRules]);

  const isOpen = noRules || !!matchedRule;

  const sanitize = useCallback(
    (input) => (matchedRule ? stripTriggers(input, matchedRule) : input),
    [matchedRule]
  );

  return { isOpen, activeRule: matchedRule, sanitize };
}
